import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, BadgeCheck, CalendarClock, CheckCheck, QrCode, RefreshCw, Trees } from 'lucide-react'
import { StatusPill } from '../components/StatusPill'
import { api } from '../lib/api'
import { formatDate, number, percent } from '../lib/format'

type Summary = { planted?: number; verified_alive?: number; dead?: number; assessed?: number; overdue?: number }
type Campaign = Summary & { id: string; name: string; status: string; target_trees?: number }
type CareTask = { id: string; title: string; status: string; priority: string; tree_code?: string; plot_name?: string; due_at?: string }

/* THESIS: A clear operational workspace gives evidence and work priority.
   OWN-WORLD: Cool white, slate type, teal actions, flat readable rows.
   STORY: Understand recorded trees, inspect campaign evidence, act on care.
   FIRST VIEWPORT: Title and actions, one statistic strip, wide campaign table and care queue.
   FORM: Approved A, civic operations workspace; seed ccb8005d. */
export function DashboardLive() {
  const [data, setData] = useState<{ summary: Summary; campaigns: Campaign[]; care: CareTask[] } | null>(null)
  const [error, setError] = useState('')
  const [revision, setRevision] = useState(0)

  useEffect(() => {
    let live = true
    setError('')
    setData(null)
    Promise.all([api.dashboard(), api.campaigns(), api.careTasks()])
      .then(([summary, campaigns, care]) => { if (live) setData({ summary, campaigns, care }) })
      .catch((e) => { if (live) setError(e instanceof Error ? e.message : String(e)) })
    return () => { live = false }
  }, [revision])

  const summary = data?.summary
  const planted = Number(summary?.planted || 0)
  const alive = Number(summary?.verified_alive || 0)
  const assessed = Number(summary?.assessed || 0)
  const dead = Number(summary?.dead || 0)
  const overdue = Number(summary?.overdue || 0)
  const openCare = data?.care.filter((task) => task.status !== 'closed') ?? []

  return <div className="page-stack">
    <section className="page-heading">
      <h2>Plantation overview</h2>
      <div className="hero-actions">
        <Link to="/field/activate" className="button button--primary"><QrCode size={18} />Activate tags</Link>
        <Link to="/verification" className="button button--secondary"><BadgeCheck size={18} />Review evidence</Link>
      </div>
    </section>

    {error ? <section className="dashboard-error panel" role="alert">
      <h3>Overview unavailable</h3><p>{error}</p>
      <button className="button button--secondary" onClick={() => setRevision((value) => value + 1)}><RefreshCw size={16} />Try again</button>
    </section> : !data ? <section className="panel empty-state" role="status"><Trees size={28} /><p>Loading plantation records…</p></section> : <>
      <section className="overview-summary" aria-label="Plantation totals">
        <div className="overview-stat"><strong>{number.format(planted)}</strong><span>Trees recorded</span></div>
        <div className="overview-stat"><strong>{number.format(alive)}</strong><span><i className="legend-dot is-alive" />Verified alive</span></div>
        <div className="overview-stat"><strong>{number.format(Math.max(0, planted - assessed))}</strong><span><i className="legend-dot is-unknown" />Awaiting assessment</span></div>
        <div className="overview-stat"><strong>{number.format(dead)}</strong><span><i className="legend-dot is-dead" />Confirmed dead</span></div>
      </section>

      <div className="overview-grid">
        <section className="panel">
          <div className="section-heading"><h3>Campaign performance</h3><Link to="/campaigns" className="text-link">All campaigns<ArrowRight size={16} /></Link></div>
          {data.campaigns.length ? <>
            <div className="campaign-table-wrap" tabIndex={0} role="region" aria-label="Campaign evidence table">
              <table className="campaign-table">
                <thead><tr><th scope="col">Campaign</th><th scope="col">Recorded</th><th scope="col">Verified alive</th><th scope="col">Awaiting assessment</th><th scope="col">Confirmed dead</th></tr></thead>
                <tbody>{data.campaigns.map((campaign) => {
                  const recorded = Number(campaign.planted || 0)
                  const verified = Number(campaign.verified_alive || 0)
                  const reviewed = Number(campaign.assessed || 0)
                  return <tr key={campaign.id}>
                    <th scope="row"><Link to="/campaigns">{campaign.name}</Link><small>Target {number.format(Number(campaign.target_trees || 0))}</small><StatusPill value={campaign.status} compact /></th>
                    <td>{number.format(recorded)}</td>
                    <td><strong>{number.format(verified)}</strong><small>{recorded ? `${percent(verified, recorded)}% of recorded` : 'No records yet'}</small></td>
                    <td>{number.format(Math.max(0, recorded - reviewed))}</td>
                    <td>{number.format(Number(campaign.dead || 0))}</td>
                  </tr>
                })}</tbody>
              </table>
            </div>
            <div className="survival-legend"><BadgeCheck size={16} /><span>{number.format(assessed)} of {number.format(planted)} recorded trees assessed</span><strong>{planted ? `${percent(assessed, planted)}% coverage` : 'No records yet'}</strong></div>
          </> : <div className="empty-state"><Trees size={30} /><h3>No campaigns yet</h3><Link to="/campaigns" className="button button--secondary">Create a campaign<ArrowRight size={16} /></Link></div>}
        </section>

        <section className="panel">
          <div className="section-heading"><h3>Care queue</h3><Link to="/care" className="text-link">View all<ArrowRight size={16} /></Link></div>
          {overdue > 0 && <Link to="/care" className="queue-overdue"><CalendarClock size={18} /><strong>{number.format(overdue)} overdue care {overdue === 1 ? 'task' : 'tasks'}</strong><ArrowRight size={16} /></Link>}
          {openCare.length ? <div className="task-list">{openCare.slice(0, 6).map((task) => <Link to="/care" className="task-item" key={task.id}>
            <div><strong>{task.title}</strong><span>{task.tree_code || task.plot_name || 'Plantation task'}</span></div>
            <div><StatusPill value={task.priority} compact /><StatusPill value={task.status} compact />{task.due_at && <small>Due {formatDate(task.due_at)}</small>}</div>
          </Link>)}</div> : <div className="empty-state"><CheckCheck size={30} /><h3>No open care tasks</h3><Link to="/care" className="text-link">Create a care task<ArrowRight size={16} /></Link></div>}
        </section>
      </div>
    </>}
  </div>
}
